import React, { useState, useRef, useEffect } from "react";
import { AlertCircle, KeyRound, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

interface TwoFactorVerificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerify?: (code: string) => Promise<{ success: boolean; error?: string }>;
  onResendCode?: () => Promise<{ success: boolean; error?: string }>;
  email?: string;
  password?: string;
  redirectUrl?: string;
}

export const TwoFactorVerificationDialog = ({
  open,
  onOpenChange,
  onVerify,
  email,
  password,
  redirectUrl
}: TwoFactorVerificationDialogProps) => {
  const navigate = useNavigate();
  const { loginWithOtp } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleVerify = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    setError("");
    
    try {
      // 直接使用loginWithOtp进行登录
      if (email && password) {
        try {
          // 使用EMAIL_PWD_OTP登录
          await loginWithOtp(email, code, password);
          setSuccess(true);
          toast.success("Verification successful");
          // 延迟关闭对话框，给用户提供视觉反馈
          setTimeout(() => {
            onOpenChange(false);
            // 使用React Router进行导航，检查是否有redirectUrl
            const targetUrl = redirectUrl || '/profile';
            navigate(targetUrl, { replace: true });
          }, 1500);
        } catch (error: any) {
          // 如果登录失败，显示错误消息
          setError(error.message || "The code is incorrect");
          toast.error(error.message || "The code is incorrect");
        }
      } else {
        // 如果没有提供邮箱或密码，显示错误消息
        setError("Missing email or password");
        toast.error("Missing email or password");
      }
    } catch (error) {
      setError("An error occurred during verification");
      toast.error("An error occurred during verification");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset state when dialog closes
      setTimeout(() => {
        setCode("");
        setError("");
        setSuccess(false);
      }, 300); // Delay reset to allow for exit animation
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="bg-black/80 border-white/10 text-white p-6 max-w-md mx-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Two-Factor Verification</DialogTitle>
          <DialogDescription className="text-gray-400 mt-2">
            Please enter the verification code from your Google Authenticator app to continue.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-6 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
              <CheckCircle className="h-6 w-6 text-green-500" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">Verification Successful</h3>
            <p className="text-gray-400 mb-4">
              Your identity has been verified.
            </p>
            <Button
              type="button"
              className="bg-[#ff6c24] hover:bg-[#ff6c24]/90 text-white w-full"
              onClick={() => handleDialogChange(false)}
            >
              Continue
            </Button>
          </div>
        ) : (
          <form onSubmit={(e) => handleVerify(e)} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="verification-code">Verification Code</Label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <KeyRound className="h-5 w-5" />
                </div>
                <input
                  ref={inputRef}
                  id="verification-code"
                  type="text"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.replace(/[^0-9]/g, ''));
                    setError("");
                  }}
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  className="w-full h-12 pl-10 pr-4 bg-black/50 border border-white/20 rounded-md 
                            text-white placeholder:text-gray-500
                            focus:outline-none focus:ring-2 focus:ring-[#ff6c24]/50 focus:border-[#ff6c24]"
                />
              </div>
              
              {error && (
                <p className="text-red-500 text-sm flex items-center gap-1 mt-1">
                  <AlertCircle size={14} /> {error}
                </p>
              )}
            </div>
            
            <DialogFooter className="mt-6 flex-col sm:flex-row gap-2">
              <Button 
                type="submit" 
                className="w-full mt-4" 
                disabled={!code.trim() || isLoading}
              >
                {isLoading ? "Verifying..." : "Log In"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
